import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import AdminAddProgram from "../components/Admin/AdminAddProgram";
import AdminAddTraseu from "../components/Admin/AdminAddTraseu";
import AdminAddAbonament from "../components/Admin/AdminAddAbonament";
import AdminUploadPoza from "../components/Admin/AdminUploadPoza";
import AdminStats from "../components/Admin/AdminStats";
import AdminProgressTracker from "../components/Admin/AdminProgressTracker";
import AdminLatest from "../components/Admin/AdminLatest";
import AdminSidebar from "../components/Admin/AdminSidebar";
import { onAuthStateChanged } from "firebase/auth";

import { auth, db } from "../firebaseConfig";
import ro from "/src/content/ro.json";
import {
  collection,
  onSnapshot,
  getDocs,
} from "firebase/firestore";
import { parseDateParts, toDateNumber, toDisplayDate } from "../utils/date";

const AdminPanel = () => {
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [abonamente, setAbonamente] = useState([]);
  const [selectedTraseu, setSelectedTraseu] = useState("MAI");
  const [totalPersoane, setTotalPersoane] = useState(0);
  const [nextTraining, setNextTraining] = useState(null);
  const [adminUser, setAdminUser] = useState(null);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (user) => {
      setAdminUser(user || null);
    });
    return () => unsub();
  }, []);

  // 🔹 Abonamente ordonate după expirare
  useEffect(() => {
    const unsubAbonamente = onSnapshot(collection(db, "abonamente"), (snapshot) => {
      const data = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      data.sort((a, b) => {
        const aNum = toDateNumber(a.dataFinal || a.dataExpirare || "") || 0;
        const bNum = toDateNumber(b.dataFinal || b.dataExpirare || "") || 0;
        return aNum - bNum;
      });
      setAbonamente(data);
    });
    return () => unsubAbonamente();
  }, []);

  // 🔹 Actualizăm datele când schimbăm traseul
  useEffect(() => {
    // 🔸 Ascultăm înscrierile la traseu
    const unsubTrasee = onSnapshot(
      collection(db, "inscrieri_traseu"),
      (snapshot) => {
        const inscrieri = snapshot.docs
          .map((doc) => doc.data())
          .filter((d) => (d.tipTraseu || "").toUpperCase() === selectedTraseu);
        setTotalPersoane(inscrieri.length);
      },
    );

    // 🔸 Ascultăm programele (toate, dar filtrăm local)
    const unsubProgram = onSnapshot(
      collection(db, "stabilire_program"),
      (snapshot) => {
        const programe = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        // 🔸 Filtrăm după tip program (MAI, ISU etc.)
        const filtered = programe.filter(
          (p) => (p.tipProgram || "").toUpperCase() === selectedTraseu,
        );

        if (filtered.length === 0) {
          setNextTraining(null);
          return;
        }

        // 🔸 Convertim data + ora în obiect Date
        const now = new Date();
        const future = filtered
          .map((p) => {
            try {
              if (!p.data) return null;
              const dateParts = parseDateParts(p.data);
              if (!dateParts) return null;
              const [hh, mm] = (p.ora || "00:00").split(":").map(Number);
              return {
                ...p,
                dateObj: new Date(
                  dateParts.y,
                  dateParts.m - 1,
                  dateParts.d,
                  hh,
                  mm
                ),
              };
            } catch {
              return null;
            }
          })
          .filter((p) => p && !isNaN(p.dateObj))
          .sort((a, b) => a.dateObj - b.dateObj);

        // 🔸 Căutăm următorul antrenament viitor
        const upcoming = future.find((p) => p.dateObj >= now) || future[0];
        setNextTraining(upcoming || null);
      },
    );

    return () => {
      unsubTrasee();
      unsubProgram();
    };
  }, [selectedTraseu]);

  // 🔹 Export CSV cu antet clar și numerotare
  const exportInscrieriCSV = async () => {
    try {
      const snapshot = await getDocs(collection(db, "inscrieri_traseu"));
      const participanti = snapshot.docs
        .map((doc) => doc.data())
        .filter(
          (p) =>
            (p.tipTraseu || "").toUpperCase() === selectedTraseu.toUpperCase(),
        );

      if (participanti.length === 0) {
        alert(
          ro.admin.panel.export.noParticipants.replace(
            "{traseu}",
            selectedTraseu
          )
        );
        return;
      }

      // Utilitar pentru a lua fie "numePrenume", fie "nume", fie ""
      const getVal = (obj, ...keys) => {
        for (let k of keys) {
          if (obj[k]) return obj[k];
        }
        return "";
      };

      // 🔹 antet CSV
      const header = [
        "Nr. Crt.",
        "Nume Prenume",
        "Email",
        "Telefon",
        "Varsta",
        "Inaltime (cm)",
        "Greutate (kg)",
        "Tip Traseu",
        "Data Traseu",
      ];

      // 🔹 generăm rândurile
      const rows = participanti.map((p, index) => [
        index + 1,
        getVal(p, "numePrenume", "nume"),
        p.email || "",
        getVal(p, "telefon", "nrTelefon"),
        p.varsta || "",
        p.inaltime || "",
        p.greutate || "",
        p.tipTraseu || "",
        p.dataTraseu || "",
      ]);

      // 🔹 combinăm într-un CSV
      const csvContent = [header, ...rows]
        .map((r) =>
          r.map((x) => `"${String(x).replace(/"/g, '""')}"`).join(","),
        )
        .join("\n");

      // 🔹 descarcă fișierul
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");

      link.href = url;
      const fileDate = toDisplayDate(new Date(), "").replace(/\//g, "-");
      link.download = `Participanti_${selectedTraseu}_${fileDate}.csv`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      alert(ro.admin.panel.export.success);
    } catch (err) {
      console.error("❌ Eroare la export:", err);
      alert(ro.admin.panel.export.error);
    }
  };

  const formatDate = (value) => toDisplayDate(value);

  return (
    <main className="admin-page">
      {/* NAVBAR (mobil / tabletă) */}
      <header className="admin-navbar">
        <span className="admin-navbar-title">{ro.admin.panel.title}</span>
        <button
          className="admin-navbar-button"
          onClick={() => setDrawerOpen((prev) => !prev)}
        >
          {ro.admin.panel.menu}
        </button>
      </header>

      {/* ACTIUNI RAPIDE (mobil) */}
      <div className="hidden px-4 py-3 flex-wrap gap-2">
        <button
          className="px-4 py-2 rounded-lg font-semibold bg-blue-600 text-white"
          onClick={() =>
            navigate(
              `/admin/prezenta?tip=${encodeURIComponent(selectedTraseu || "")}`,
            )
          }
        >
          {ro.admin.panel.actions.attendance}
        </button>
        <button
          className="px-4 py-2 rounded-lg font-semibold bg-orange-600 text-white"
          onClick={exportInscrieriCSV}
        >
          {ro.admin.panel.actions.exportCsv}
        </button>
      </div>

      {/* SIDEBAR */}
      <AdminSidebar
        drawerOpen={drawerOpen}
        adminUser={adminUser}
        exportInscrieriCSV={exportInscrieriCSV}
        abonamente={abonamente}
        formatDate={formatDate}
        selectedTraseu={selectedTraseu}
        setSelectedTraseu={setSelectedTraseu}
        nextTraining={nextTraining}
        totalPersoane={totalPersoane}
      />

      {/* CONTINUT */}
      <section className="admin-content">
        <section className="admin-dashboard-layout">
          {/* RÂND 1 */}
          <div className="admin-row full">
            <AdminStats />
          </div>

          {/* RÂND 2 */}
          <div className="admin-row">
            <div className="admin-col gap-5">
              <AdminAddProgram />
            </div>
            <div className="admin-col gap-5">
              <AdminProgressTracker />
            </div>
            <div className="admin-col gap-5">
              <AdminUploadPoza />
            </div>
          </div>

          {/* RÂND 3 */}
          <div className="admin-row">
            <div className="admin-col gap-5">
              <AdminLatest />
            </div>
            <div className="admin-col gap-5">
              <AdminAddAbonament />
            </div>
            <div className="admin-col gap-5">
              <AdminAddTraseu />
            </div>
          </div>
        </section>
      </section>
    </main>
  );
};

export default AdminPanel;
